import React, { useState, useEffect, useCallback } from 'react';
import { Search, Plus, X, Save, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, MessageSquare, Wrench, DollarSign, AlertCircle, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  Open: 'bg-teal-500/15 text-teal-400 border-teal-500/30',
  Pending: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  Completed: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
};

function StatusBadge({ status }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_COLORS[status] || 'bg-zinc-500/15 text-zinc-400'}`}>{status}</span>;
}

function CostPill({ label, value, color }) {
  if (!value) return null;
  return <span className={`text-xs px-2 py-0.5 rounded bg-${color}-500/10 text-${color}-400 font-mono`}>{label}: ${value.toFixed(2)}</span>;
}

export default function WorkOrdersPage() {
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterVehicle, setFilterVehicle] = useState('');
  const [statusCounts, setStatusCounts] = useState({});
  const [expandedId, setExpandedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalValue, setTotalValue] = useState(0);

  // Create form
  const [newWO, setNewWO] = useState({ vehicle_name: '', description: '', contact_name: '', issued_by: '' });

  // Inline add forms
  const [newIssue, setNewIssue] = useState('');
  const [newComment, setNewComment] = useState('');
  const [newItem, setNewItem] = useState({ type: 'part', description: '', part_number: '', category: '', quantity: 1, unit_cost: 0, contact_name: '' });
  const [showAddItem, setShowAddItem] = useState(false);
  const [editingStatus, setEditingStatus] = useState(null);
  const [technicians, setTechnicians] = useState([]);

  const token = localStorage.getItem('token');
  const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };
  const authHeaders = { 'Authorization': `Bearer ${token}` };

  const fetchWOs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterVehicle) params.set('vehicle', filterVehicle);
      params.set('page', page);
      params.set('per_page', 25);
      const r = await fetch(`${API}/api/work-orders?${params}`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) {
        setWorkOrders(d.work_orders);
        setStatusCounts(d.status_counts || {});
        setTotal(d.total || d.count);
        setTotalPages(d.pages || 1);
        setTotalValue(d.total_value || 0);
      }
    } catch { toast.error('Failed to load work orders'); }
    finally { setLoading(false); }
  }, [search, filterStatus, filterVehicle, page, token]);

  const fetchVehicles = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/vehicles`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setVehicles(d.vehicles.map(v => v.name).sort());
    } catch {}
  }, [token]);

  const fetchTechnicians = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/technicians`, { headers: authHeaders });
      const d = await r.json();
      if (d.success) setTechnicians(d.technicians);
    } catch {}
  }, [token]);

  useEffect(() => { fetchWOs(); }, [fetchWOs]);
  useEffect(() => { fetchVehicles(); }, [fetchVehicles]);
  useEffect(() => { fetchTechnicians(); }, [fetchTechnicians]);

  const handleCreate = async () => {
    if (!newWO.vehicle_name) { toast.error('Select a vehicle'); return; }
    try {
      const r = await fetch(`${API}/api/work-orders`, { method: 'POST', headers, body: JSON.stringify(newWO) });
      if (!r.ok) { toast.error('Failed to create'); return; }
      const d = await r.json();
      toast.success(`Work Order ${d.work_order.number} created`);
      setCreating(false);
      setNewWO({ vehicle_name: '', description: '', contact_name: '', issued_by: '' });
      fetchWOs();
      setExpandedId(d.work_order.id);
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id, num) => {
    if (!window.confirm(`Delete ${num}?`)) return;
    try { await fetch(`${API}/api/work-orders/${id}`, { method: 'DELETE', headers }); toast.success('Deleted'); fetchWOs(); }
    catch { toast.error('Failed'); }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await fetch(`${API}/api/work-orders/${id}`, { method: 'PUT', headers, body: JSON.stringify({ status: newStatus }) });
      toast.success(`Status updated to ${newStatus}`);
      setEditingStatus(null);
      fetchWOs();
    } catch { toast.error('Failed'); }
  };

  const handleAddIssue = async (woId) => {
    if (!newIssue.trim()) return;
    try {
      await fetch(`${API}/api/work-orders/${woId}/issues`, { method: 'POST', headers, body: JSON.stringify({ name: newIssue }) });
      setNewIssue('');
      fetchWOs();
    } catch { toast.error('Failed'); }
  };

  const handleRemoveIssue = async (woId, issueId) => {
    try { await fetch(`${API}/api/work-orders/${woId}/issues/${issueId}`, { method: 'DELETE', headers }); fetchWOs(); }
    catch { toast.error('Failed'); }
  };

  const handleAddLineItem = async (woId) => {
    if (newItem.type === 'part' && !newItem.part_number && !newItem.description) { toast.error('Enter part details'); return; }
    if (newItem.type === 'labor' && !newItem.contact_name) { toast.error('Enter who did the work'); return; }
    try {
      await fetch(`${API}/api/work-orders/${woId}/line-items`, { method: 'POST', headers, body: JSON.stringify(newItem) });
      setNewItem({ type: 'part', description: '', part_number: '', category: '', quantity: 1, unit_cost: 0, contact_name: '' });
      setShowAddItem(false);
      fetchWOs();
    } catch { toast.error('Failed'); }
  };

  const handleRemoveLineItem = async (woId, itemId) => {
    try { await fetch(`${API}/api/work-orders/${woId}/line-items/${itemId}`, { method: 'DELETE', headers }); fetchWOs(); }
    catch { toast.error('Failed'); }
  };

  const handleAddComment = async (woId) => {
    if (!newComment.trim()) return;
    try {
      await fetch(`${API}/api/work-orders/${woId}/comments`, { method: 'POST', headers, body: JSON.stringify({ comment: newComment }) });
      setNewComment('');
      fetchWOs();
    } catch { toast.error('Failed'); }
  };

  const uniqueVehicles = [...new Set(workOrders.map(w => w.vehicle_name))].sort();

  return (
    <div data-testid="work-orders-page" className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        {['Open', 'Pending', 'Completed'].map(s => (
          <button key={s} onClick={() => setFilterStatus(filterStatus === s ? '' : s)}
            className={`bg-card border rounded-lg p-3 text-left transition-colors ${filterStatus === s ? 'border-primary' : ''}`}>
            <div className="text-xs text-muted-foreground">{s}</div>
            <div className="text-xl font-bold">{statusCounts[s] || 0}</div>
          </button>
        ))}
        <div className="bg-card border rounded-lg p-3">
          <div className="text-xs text-muted-foreground">Total Value</div>
          <div className="text-xl font-bold text-emerald-400">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</div>
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="wo-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search WO#, vehicle, issue..." className="pl-9" />
        </div>
        <select value={filterVehicle} onChange={e => { setFilterVehicle(e.target.value); setPage(1); }} className="h-9 rounded-md border border-input bg-background px-3 text-sm">
          <option value="">All Vehicles</option>
          {uniqueVehicles.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
        {(filterStatus || filterVehicle) && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterVehicle(''); setPage(1); }}>Clear</Button>
        )}
        <Button data-testid="create-wo-btn" onClick={() => setCreating(true)} size="sm"><Plus className="h-4 w-4 mr-1" />New Work Order</Button>
      </div>

      {/* Create Form */}
      {creating && (
        <div className="bg-card border rounded-xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">New Work Order</h2>
            <Button variant="ghost" size="icon" onClick={() => setCreating(false)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Vehicle *</label>
              <select value={newWO.vehicle_name} onChange={e => setNewWO(p => ({ ...p, vehicle_name: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Select vehicle...</option>
                {vehicles.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Issued By</label>
              <Input value={newWO.issued_by} onChange={e => setNewWO(p => ({ ...p, issued_by: e.target.value }))} placeholder="Your name" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Contact / Assigned To</label>
              <select value={newWO.contact_name} onChange={e => setNewWO(p => ({ ...p, contact_name: e.target.value }))}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
                <option value="">Select tech...</option>
                {technicians.map(t => <option key={t.id} value={t.name}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Description</label>
              <Input value={newWO.description} onChange={e => setNewWO(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handleCreate} size="sm"><Save className="h-4 w-4 mr-1" />Create</Button>
            <Button variant="outline" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {/* Work Orders List */}
      {loading ? <div className="text-center py-8 text-muted-foreground">Loading...</div> : (
        <div className="space-y-2">
          {workOrders.map(wo => {
            const isExpanded = expandedId === wo.id;
            return (
              <div key={wo.id} data-testid={`wo-${wo.number}`} className="border rounded-xl overflow-hidden">
                {/* Header Row */}
                <div className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/10 transition-colors"
                  onClick={() => setExpandedId(isExpanded ? null : wo.id)}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-mono font-bold text-sm">{wo.number}</span>
                    <span className="font-bold text-sm">{wo.vehicle_name}</span>
                    <StatusBadge status={wo.status} />
                    {wo.issues?.length > 0 && (
                      <span className="text-xs text-muted-foreground hidden sm:inline truncate max-w-[300px]">
                        {wo.issues.map(i => i.name).join(', ')}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {wo.total_amount > 0 && <span className="font-mono text-sm font-bold text-emerald-400">${wo.total_amount.toFixed(2)}</span>}
                    {wo.comments?.length > 0 && <span className="text-xs text-muted-foreground flex items-center gap-1"><MessageSquare className="h-3 w-3" />{wo.comments.length}</span>}
                    <span className="text-xs text-muted-foreground">{wo.issued_by}</span>
                    <span className="text-xs text-muted-foreground">{(wo.issued_at || '').substring(0, 10)}</span>
                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>

                {/* Expanded Detail */}
                {isExpanded && (
                  <div className="border-t p-4 space-y-4 bg-muted/5">
                    {/* Meta */}
                    <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                      <span>Issued by: <strong className="text-foreground">{wo.issued_by || '—'}</strong></span>
                      <span>Contact: <strong className="text-foreground">{wo.contact_name || '—'}</strong></span>
                      <span>Issued: {(wo.issued_at || '').substring(0, 10)}</span>
                      {wo.completed_at && <span>Completed: {wo.completed_at.substring(0, 10)}</span>}
                      <div className="flex items-center gap-1">
                        Status:
                        {editingStatus === wo.id ? (
                          <select autoFocus value={wo.status} onChange={e => handleStatusChange(wo.id, e.target.value)} onBlur={() => setEditingStatus(null)}
                            className="h-6 rounded border border-input bg-background px-2 text-xs">
                            {['Open', 'Pending', 'Completed'].map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                        ) : (
                          <button onClick={() => setEditingStatus(wo.id)} className="underline decoration-dotted"><StatusBadge status={wo.status} /></button>
                        )}
                      </div>
                    </div>

                    {/* Issues */}
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />Issues</h4>
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(wo.issues || []).map(iss => (
                          <span key={iss.id} className="text-xs px-2 py-1 rounded-lg bg-amber-500/10 text-amber-300 border border-amber-500/20 flex items-center gap-1">
                            {iss.name}
                            <button onClick={() => handleRemoveIssue(wo.id, iss.id)} className="hover:text-red-400"><X className="h-3 w-3" /></button>
                          </span>
                        ))}
                        {(!wo.issues || wo.issues.length === 0) && <span className="text-xs text-muted-foreground">No issues</span>}
                      </div>
                      <div className="flex gap-2">
                        <Input className="h-7 text-xs flex-1" placeholder="Add issue..." value={newIssue} onChange={e => setNewIssue(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddIssue(wo.id); }} />
                        <Button size="sm" className="h-7 text-xs" onClick={() => handleAddIssue(wo.id)}>Add</Button>
                      </div>
                    </div>

                    {/* Line Items */}
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />Parts & Labor</h4>
                      {(wo.line_items || []).length > 0 ? (
                        <table className="w-full text-xs mb-2">
                          <thead>
                            <tr className="border-b text-muted-foreground">
                              <th className="text-left py-1 pr-2">Type</th>
                              <th className="text-left py-1 pr-2">Part # / Contact</th>
                              <th className="text-left py-1 pr-2">Description</th>
                              <th className="text-left py-1 pr-2">Category</th>
                              <th className="text-right py-1 pr-2">Qty</th>
                              <th className="text-right py-1 pr-2">Unit $</th>
                              <th className="text-right py-1 pr-2">Total</th>
                              <th className="w-8"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {wo.line_items.map(li => (
                              <tr key={li.id} className="border-b border-border/30">
                                <td className="py-1 pr-2"><span className={`px-1.5 py-0.5 rounded text-xs ${li.type === 'part' ? 'bg-blue-500/10 text-blue-400' : 'bg-purple-500/10 text-purple-400'}`}>{li.type}</span></td>
                                <td className="py-1 pr-2 font-mono">{li.type === 'labor' ? li.contact_name : li.part_number}</td>
                                <td className="py-1 pr-2 max-w-[200px] truncate">{li.description}</td>
                                <td className="py-1 pr-2 text-muted-foreground">{li.category}</td>
                                <td className="py-1 pr-2 text-right">{li.quantity}</td>
                                <td className="py-1 pr-2 text-right font-mono">${(li.unit_cost || 0).toFixed(2)}</td>
                                <td className="py-1 pr-2 text-right font-mono font-bold">${((li.quantity || 0) * (li.unit_cost || 0)).toFixed(2)}</td>
                                <td><button onClick={() => handleRemoveLineItem(wo.id, li.id)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3 w-3" /></button></td>
                              </tr>
                            ))}
                            <tr className="font-bold">
                              <td colSpan={6} className="py-1 text-right">Totals:</td>
                              <td className="py-1 text-right font-mono">
                                <div className="text-blue-400">Parts: ${(wo.parts_subtotal || 0).toFixed(2)}</div>
                                <div className="text-purple-400">Labor: ${(wo.labor_subtotal || 0).toFixed(2)}</div>
                                <div className="text-emerald-400 border-t border-border/50 pt-0.5">${(wo.total_amount || 0).toFixed(2)}</div>
                              </td>
                              <td></td>
                            </tr>
                          </tbody>
                        </table>
                      ) : <p className="text-xs text-muted-foreground mb-2">No line items</p>}

                      {showAddItem ? (
                        <div className="border rounded-lg p-2 space-y-2 bg-muted/10">
                          <div className="flex gap-2 flex-wrap items-end">
                            <div>
                              <label className="text-xs text-muted-foreground block">Type</label>
                              <select value={newItem.type} onChange={e => setNewItem(p => ({ ...p, type: e.target.value }))}
                                className="h-7 rounded border border-input bg-background px-2 text-xs w-20">
                                <option value="part">Part</option>
                                <option value="labor">Labor</option>
                              </select>
                            </div>
                            {newItem.type === 'part' ? (
                              <>
                                <div className="flex-1 min-w-[120px]"><label className="text-xs text-muted-foreground block">Part #</label>
                                  <Input className="h-7 text-xs" value={newItem.part_number} onChange={e => setNewItem(p => ({ ...p, part_number: e.target.value }))} /></div>
                                <div className="flex-1 min-w-[120px]"><label className="text-xs text-muted-foreground block">Description</label>
                                  <Input className="h-7 text-xs" value={newItem.description} onChange={e => setNewItem(p => ({ ...p, description: e.target.value }))} /></div>
                                <div className="w-24"><label className="text-xs text-muted-foreground block">Category</label>
                                  <Input className="h-7 text-xs" value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))} placeholder="BRAKES" /></div>
                              </>
                            ) : (
                              <div className="flex-1 min-w-[150px]"><label className="text-xs text-muted-foreground block">Technician</label>
                                <select value={newItem.contact_name} onChange={e => {
                                  const tech = technicians.find(t => t.name === e.target.value);
                                  setNewItem(p => ({ ...p, contact_name: e.target.value, unit_cost: tech ? tech.hourly_rate : p.unit_cost }));
                                }} className="flex h-7 w-full rounded-md border border-input bg-background px-2 text-xs">
                                  <option value="">Select tech...</option>
                                  {technicians.map(t => <option key={t.id} value={t.name}>{t.name} (${t.hourly_rate}/hr)</option>)}
                                </select></div>
                            )}
                            <div className="w-16"><label className="text-xs text-muted-foreground block">Qty</label>
                              <Input className="h-7 text-xs" type="number" value={newItem.quantity} onChange={e => setNewItem(p => ({ ...p, quantity: parseFloat(e.target.value) || 0 }))} /></div>
                            <div className="w-24"><label className="text-xs text-muted-foreground block">Unit $</label>
                              <Input className="h-7 text-xs" type="number" step="0.01" value={newItem.unit_cost} onChange={e => setNewItem(p => ({ ...p, unit_cost: parseFloat(e.target.value) || 0 }))} /></div>
                            <Button size="sm" className="h-7 text-xs" onClick={() => handleAddLineItem(wo.id)}>Add</Button>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setShowAddItem(false)}>Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowAddItem(true)}><Plus className="h-3.5 w-3.5 mr-1" />Add Part or Labor</Button>
                      )}
                    </div>

                    {/* Comments */}
                    <div>
                      <h4 className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" />Comments</h4>
                      <div className="space-y-1.5 mb-2 max-h-40 overflow-y-auto">
                        {(wo.comments || []).map(c => (
                          <div key={c.id} className="text-xs border-l-2 border-border/50 pl-2 py-0.5">
                            <span className="text-muted-foreground">{(c.created_at || '').substring(0, 10)}</span>
                            {c.author && <span className="text-muted-foreground"> — {c.author}</span>}
                            <p className="mt-0.5">{c.comment}</p>
                          </div>
                        ))}
                        {(!wo.comments || wo.comments.length === 0) && <p className="text-xs text-muted-foreground">No comments</p>}
                      </div>
                      <div className="flex gap-2">
                        <Input className="h-7 text-xs flex-1" placeholder="Add a comment..." value={newComment} onChange={e => setNewComment(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddComment(wo.id); }} />
                        <Button size="sm" className="h-7 text-xs" onClick={() => handleAddComment(wo.id)}><Send className="h-3 w-3 mr-1" />Post</Button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 border-t pt-2">
                      <Button variant="ghost" size="sm" className="text-xs text-red-400 hover:text-red-300" onClick={() => handleDelete(wo.id, wo.number)}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {workOrders.length === 0 && <div className="text-center py-8 text-muted-foreground">No work orders found</div>}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, total)} of {total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="h-4 w-4" /></Button>
            <span className="px-3 py-1">Page {page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
    </div>
  );
}
